import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  Button,
  closeModal,
  controlToastState,
  Footer,
} from "risa-oasis-ui_v2";
import {
  editCoverMyMedsRequest,
  sendtoPlanRequest,
} from "../../api/bigQuery/nycbsPharmaOrders";
import { addCmmEvent } from "../../api/firebase/firestoreService";
import FormSections from "../../components/formSections/formSections";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import AsterasubmissionModal from "../../components/modals/asteraSubmission/asteraSubmission";
import DrugLabelModal from "../../components/modals/drugLabelModal/drugLabelModal";
import InsuranceDetailsErrorModal from "../../components/modals/insuranceDetailsErrorModal/insuranceDetailsErrorModal";
import InsuranceModal from "../../components/modals/insuranceModal/insuranceModal";
import SendToPlanConfirmModal from "../../components/modals/sendToPlanConfirmModal/sendToPlanConfirmModal";
import SubmissionSummary from "../../components/modals/submissionSummary/submissionSummary";
import { usePharmaFormFields } from "../../context/pharmaFormFieldsContext";
import { mapToCoverMyMedsInputModel } from "../../data-model/cmmInputRequestModel";
import { OrderStatus } from "../../enums/authStatus";
import { CmmEvents } from "../../enums/cmmEvents";
import { ModalId } from "../../enums/modalId";
import { NycbsPharmaOrderKeys } from "../../enums/nycbsPharmaOrder";
import { OrganizationType } from "../../enums/organizationTypes";
import { ScreenNames } from "../../enums/screenNames";
import { useBranchedPharmaQuestions } from "../../hooks/useBranchedPharmaQuestions";
import { useModalOpener } from "../../hooks/useModalOpener";
import { fetchSingleOrderDetailsForCmm } from "../../redux/slice/cmm/cmmSingleOrderSlice";
import { fetchAllDiagnosisDetailsForAnOrder } from "../../redux/slice/cmm/dignosisDetailsSlice";
import { fetchAllQuestionFromBigQuery } from "../../redux/slice/cmm/pharmaQuestionsSlice";
import { fetchPrescriptionData } from "../../redux/slice/cmm/prescriptionSlice";
import {
  closeModal as closeModalNew,
  setOpenedModalId,
} from "../../redux/slice/modalSliceNew";
import { fetchSingleOrderDocs } from "../../redux/slice/nycbsPharmaExternal/nycbsDocumentsSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import BackArrowBlue from "../../svg/back-arrow-blue";
import { CopyIcon } from "../../svg/copy-icon";
import { calculateAge } from "../../utils/ageCalculator";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { capitalizeString } from "../../utils/stringModifications";
import { getPortalNameFromMetadata } from "../cmmOrder/table/utils/getPortalNameFromMetadata";
import { readCmmOrderAssigneeId } from "../../utils/cmmOrderAssigneeIdStorage";
import { executeSubmitPa } from "../cmmOrder/utils/executeSubmitPa";
import { handleOpenSubmissionSummary } from "../pharmaStpFile/actionHandlers/openSubmissionSummaryHandler";
import PharmaPaForm from "./components/pharmaPaForm";
import { getFooterButtonConfig } from "./utils/footerButtonConfig";
import { getUpdatedFormDataWithFilledValues } from "./utils/getUpdatedFormDataWithFilledValues";

const PharmaPaFormLayout = () => {
  const {
    isAllFieldsValidationFree,
    setShouldRefetchData,
    formFieldsData,
    isFormDirty,
    hasOpenedReportInaccuracyModal,
    setHasOpenedReportInaccuracyModal,
  } = usePharmaFormFields();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [currentFormId, setCurrentFormId] = useState<string | null>(null);
  const [fetchModalOpen, setFetchModalOpen] = useState(false);
  const [showInsuranceErrorModal, setShowInsuranceErrorModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const { metaData } = useSelector((state: RootState) => state.modalSliceNew);
  const { openedModalId } = useModalOpener();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { data, loading } = useSelector(
    (state: RootState) => state.cmmSingleOrder,
  );
  const location = useLocation();
  const assigneeIdFromNav = (
    location.state as { assignee_id?: string | null } | null
  )?.assignee_id;
  const assigneeIdFallback =
    (assigneeIdFromNav && String(assigneeIdFromNav).trim()) ||
    readCmmOrderAssigneeId(id) ||
    null;

  /**
   * Order data for Submission Summary, with `assignee_id` filled from navigation
   * state / sessionStorage when the single-order API returns it empty.
   */
  const submissionSummaryOrderData = useMemo(() => {
    if (!data) return data;
    const fromApi = data?.[NycbsPharmaOrderKeys.AssigneeId];
    if (fromApi && String(fromApi).trim() !== "") {
      return data;
    }
    if (assigneeIdFallback) {
      return {
        ...data,
        [NycbsPharmaOrderKeys.AssigneeId]: assigneeIdFallback,
      };
    }
    return data;
  }, [data, assigneeIdFallback]);

  const { data: formOptions, loading: formOptionsLoading } = useSelector(
    (state: RootState) => state.formOptions,
  );

  const orgId = getOrgIdForFetchExternalWorklist();

  const { data: formConfig, loading: formConfigLoading } = useSelector(
    (state: RootState) => state.cmmFormConfig,
  );

  // const { data: pharmaQuestionaire, loading: pharmaQuestionaireLoading } =
  //   useSelector((state: RootState) => state.pharmaQuestions);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { loading: diagnosisDetailsLoading } = useSelector(
    (state: RootState) => state.diagnosisDetails,
  );

  const { loading: singleOrderDocsLoading } = useSelector(
    (state: RootState) => state.nycbsDocuments,
  );

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { data: questionnaireReview, loading: questionnaireReviewLoading } =
    useBranchedPharmaQuestions(id ?? "");
  const [searchParams, setSearchParams] = useSearchParams();
  const isQuestionsAvailable = useMemo(() => {
    const allQuestions =
      questionnaireReview?.details?.questionnaire?.clinical_question_sections ??
      [];
    return allQuestions.length > 0;
  }, [questionnaireReview]);

  const navigate = useNavigate();

  const shouldDisableSendToPlan = () => {
    if (
      data?.[NycbsPharmaOrderKeys.CmmResultKey] === undefined ||
      data?.[NycbsPharmaOrderKeys.CmmResultKey] === null ||
      data?.[NycbsPharmaOrderKeys.CmmResultKey]?.trim() === ""
    ) {
      return true;
    }

    if (!isAllFieldsValidationFree) return true;

    if (isFormDirty) return true;

    return false;
  };

  const onFormSubmit = async (sendToPlan: boolean) => {
    const filledValues = getUpdatedFormDataWithFilledValues(formFieldsData);
    const mergedValues = { ...(data ?? {}), ...filledValues };
    const updatedFormDataWithFilledValues =
      mapToCoverMyMedsInputModel(mergedValues);

    const useSendToPlan =
      questionnaireReview === null && (sendToPlan || isPrimaryCare);

    if (useSendToPlan) {
      sendtoPlanRequest(
        updatedFormDataWithFilledValues,
        id ?? "",
        ScreenNames.PHARMA_PA_FORM,
        user?.email ?? "",
      );
      controlToastState("send-to-plan-success");
    } else {
      editCoverMyMedsRequest(
        {
          cover_my_meds_input: updatedFormDataWithFilledValues,
          send_to_plan: false,
        },
        id ?? "",
        ScreenNames.PHARMA_PA_FORM,
        user?.email ?? "",
      );
      controlToastState("form-save-success");
    }
    if (isQuestionsAvailable) {
      navigate(`/pharma-pa-worklists/pharma-pa-questionaire/${id}`);
    } else if (isPrimaryCare) {
      navigate("/pharma-pa-primary-care/primary-care-orders");
    } else {
      navigate("/pharma-pa-worklists/");
    }
  };

  const getFormConfigId = (planName: string, formConfiguration) => {
    if (!planName || planName === "") {
      return "default";
    }

    const fuzzySearchOptions = {
      includeScore: true,
      threshold: 0.3,
      distance: 200,
      useExtendedSearch: true,
      keys: ["id"],
    };

    const options = fuzzySearchOptions;
    const fuse = new Fuse(formConfiguration, options);
    const result = fuse.search(planName);
    const mostMatchedSearch = result?.[0]?.item;

    return mostMatchedSearch?.["id"] ?? "default";
  };

  useEffect(() => {
    if (formConfig && data) {
      const planNameFromDb = getFormConfigId(
        data?.[NycbsPharmaOrderKeys.PlanName] ?? "",
        formConfig,
      );
      setCurrentFormId(planNameFromDb);
    }
  }, [formConfig, data]);

  const isPrimaryCare = useMemo(() => {
    if (orgId === OrganizationType.ASTERA_PRIMARY_CARE) {
      return true;
    }

    return false;
  }, [orgId]);

  // Handle opening report inaccuracy modal from URL parameter
  useEffect(() => {
    const openReportInaccuracy = searchParams.get("openReportInaccuracy");
    if (openReportInaccuracy === "true" && !loading && data) {
      setShowInsuranceErrorModal(true);
      setHasOpenedReportInaccuracyModal(true);
      // Remove the parameter from URL
      searchParams.delete("openReportInaccuracy");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, data]);

  useEffect(() => {
    if (id) {
      dispatch(fetchSingleOrderDetailsForCmm(id));
      dispatch(fetchAllQuestionFromBigQuery(id));
      dispatch(fetchPrescriptionData(id));
      dispatch(fetchAllDiagnosisDetailsForAnOrder(id));
      dispatch(fetchSingleOrderDocs(id));
    }
  }, [id]);

  // Get the footer button configuration
  const footerButtonConfig = getFooterButtonConfig({
    data: data ?? null,
    isAllFieldsValidationFree,
    isFormDirty,
  });

  return (
    <>
      {loading ||
      formOptionsLoading ||
      formConfigLoading ||
      questionnaireReviewLoading ||
      diagnosisDetailsLoading ||
      singleOrderDocsLoading ? (
        <div className="full-screen__loader flex h-full flex-col">
          <LoaderMessage message="Fetching form information..." />
        </div>
      ) : (
        formOptions &&
        formOptions.length > 0 && (
          <div className="pharma-pa-form__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
            <div className="pharma-pa-form__header mb-4">
              <div
                className="pharma-pa-form__header header-content flex items-center gap-1 text-h12 font-bold text-tertiaryBlue-4 hover:cursor-pointer"
                onClick={() => {
                  setShouldRefetchData(true);
                  setTimeout(() => {
                    if (isPrimaryCare) {
                      navigate(
                        `/pharma-pa-primary-care/insurance-details/${id}`,
                      );
                    } else {
                      navigate(`/pharma-pa-worklists/insurance-details/${id}`);
                    }
                  }, 0);
                }}
              >
                <BackArrowBlue />
                Go Back
              </div>
            </div>
            <div className="pharma-pa-form__body flex flex-1 flex-col overflow-hidden rounded bg-white p-4 pb-0">
              <div className="pharma-pa-form__body--header mb-4">
                <div className="pharma-pa-form__body--header-details flex items-center gap-2">
                  <div className="pharma-pa-form__body page-name text-large font-semiBold leading-6 text-black">
                    Prior Authorisation Form
                  </div>
                  <div className="insurance-details__body-patient-name rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                    {capitalizeString(
                      generateFullName(
                        data?.[NycbsPharmaOrderKeys.PatientFirstName] || "",
                        "",
                        data?.[NycbsPharmaOrderKeys.PatientLastName] || "",
                      ),
                    )}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {data?.[NycbsPharmaOrderKeys.PatientMrn] ?? ""}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {data?.[NycbsPharmaOrderKeys.PatientDob] ?? ""}
                    &nbsp;&nbsp;(
                    {calculateAge(
                      data?.[NycbsPharmaOrderKeys.PatientDob] ?? "",
                    )}
                    yrs) &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {data?.[NycbsPharmaOrderKeys.DrugNameOncoEmr]?.split(
                      " ",
                    )[0] ?? ""}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    <span className="rounded-[1.875rem] bg-primaryGray-16 text-small font-semibold leading-6 text-primaryGray-1">
                      {data?.drug_cohort ?? "--"}
                    </span>
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    <span className="rounded-[1.875rem] bg-primaryGray-16 text-small font-semibold leading-6 text-primaryGray-1">
                      {data?.drug_class ?? "--"}
                    </span>
                  </div>

                  <div className="rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                    {data?.type ?? ""}
                  </div>
                  <div className="rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                    {getPortalNameFromMetadata(
                      data?.[NycbsPharmaOrderKeys.Metadata],
                    ) ?? ""}
                  </div>

                  <div className="cmm-result-key ml-auto flex items-center gap-2 text-small">
                    Cmm Key:{" "}
                    <span className="font-bold">
                      {data?.[NycbsPharmaOrderKeys.CmmResultKey] ?? ""}&nbsp;
                    </span>
                    <div
                      className="cmm-result-key copy-icon mr-2 hover:cursor-pointer"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          data?.[NycbsPharmaOrderKeys.CmmResultKey] ?? "",
                        );
                      }}
                    >
                      <CopyIcon />
                    </div>
                    <Button
                      buttonType="secondary"
                      size="small"
                      disabled={false}
                      onClick={() => {
                        setFetchModalOpen(true);
                      }}
                    >
                      Fetch Drug Usage
                    </Button>
                  </div>
                </div>
              </div>
              <div className="pharma-pa-form__body content flex flex-1 overflow-hidden">
                <div className="pharma-pa-form__body form-sections w-1/5">
                  <FormSections
                    activeSection={
                      activeSectionId ? `section-${activeSectionId}` : undefined
                    }
                    formConfiguration={formConfig ?? []}
                    matchedKey={currentFormId ?? ""}
                  />
                </div>
                <div className="pharma-pa-form__body main-form h-full w-4/5 overflow-auto overflow-x-hidden border border-primaryGray-16 p-4">
                  <PharmaPaForm
                    sectionOnClick={(value) => {
                      setActiveSectionId(value as string);
                    }}
                    docId={id ?? ""}
                  />
                </div>
              </div>
            </div>
            <div className="pharma-pa-form__footer">
              <Footer
                cancelButtonText={"Save"}
                submitButtonText={footerButtonConfig.label}
                onCancelClick={() => {
                  addCmmEvent(id ?? "", {
                    event: CmmEvents.SAVE_CLICKED_ON_FORM,
                    screen_name: ScreenNames.PHARMA_PA_FORM,
                    created_at: new Date(),
                    email: user?.email ?? "",
                  });
                  onFormSubmit(false);
                }}
                onSubmitClick={() => {
                  if (footerButtonConfig.buttonId === "view_outcome") {
                    navigate(`/pharma-pa-worklists/request-outcome/${id}`);
                  } else if (
                    footerButtonConfig.buttonId === "submit_pa" ||
                    footerButtonConfig.buttonId === "retry_submit"
                  ) {
                    void executeSubmitPa(id ?? "", () => {
                      if (id) {
                        dispatch(fetchSingleOrderDetailsForCmm(id));
                      }
                    });
                  } else if (
                    footerButtonConfig.buttonId === "retry_update" ||
                    footerButtonConfig.buttonId === "view_clinical"
                  ) {
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.CONTINUE_CLICKED_ON_FORM,
                      screen_name: ScreenNames.PHARMA_PA_FORM,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                    navigate(
                      `/pharma-pa-worklists/pharma-pa-questionaire/${id}`,
                    );
                  } else if (isQuestionsAvailable) {
                    setOpenConfirmModal(true);
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.SEND_TO_PLAN_CLICKED,
                      screen_name: ScreenNames.PHARMA_PA_FORM,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                  } else {
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.CONTINUE_CLICKED_ON_FORM,
                      screen_name: ScreenNames.PHARMA_PA_FORM,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                    navigate(
                      `/pharma-pa-worklists/pharma-pa-questionaire/${id}`,
                    );
                  }
                }}
                isDisabled={
                  isQuestionsAvailable
                    ? footerButtonConfig.disabled
                    : shouldDisableSendToPlan()
                }
                isCancelDisabled={
                  !isFormDirty && !hasOpenedReportInaccuracyModal
                }
                children={
                  <>
                    {" "}
                    <Button
                      buttonType={"secondary"}
                      size={"medium"}
                      onClick={() => {
                        if (orgId === OrganizationType.ASTERA) {
                          dispatch(
                            setOpenedModalId({
                              id: ModalId.ASTERA_SUBMISSION_MODAL,
                              metaData: {
                                data: submissionSummaryOrderData,
                              },
                            }),
                          );
                        } else {
                          handleOpenSubmissionSummary(
                            dispatch,
                            submissionSummaryOrderData,
                          );
                        }
                      }}
                      disabled={
                        isQuestionsAvailable ||
                        isFormDirty ||
                        data?.[NycbsPharmaOrderKeys.CmmStatus] ===
                          OrderStatus.FIRST_STP_OUTCOME ||
                        data?.[NycbsPharmaOrderKeys.CmmStatus] ===
                          OrderStatus.PA_SUBMITTED
                      }
                      children={"Submit Cmm Key"}
                    />
                  </>
                }
              />
              {openConfirmModal && (
                <SendToPlanConfirmModal
                  onClose={() => setOpenConfirmModal(false)}
                  onSaveClick={() => {
                    addCmmEvent(id ?? "", {
                      event: CmmEvents.SEND_TO_PLAN_MODAL_CONFIRM,
                      screen_name: ScreenNames.SEND_TO_PLAN_MODAL,
                      created_at: new Date(),
                      email: user?.email ?? "",
                    });
                    onFormSubmit(true);
                  }}
                />
              )}
              {fetchModalOpen && (
                <DrugLabelModal
                  onClose={() => {
                    setFetchModalOpen(false);
                    closeModal("drug-label-modal");
                  }}
                />
              )}
              {showInsuranceErrorModal && (
                <InsuranceDetailsErrorModal
                  data={data ?? {}}
                  onCloseModal={() => setShowInsuranceErrorModal(false)}
                  onSuccessfullSave={() => {
                    setShowInsuranceErrorModal(false);
                    setShowInsuranceModal(true);
                  }}
                />
              )}
              {showInsuranceModal && (
                <InsuranceModal
                  isModalOpen={(value) => setShowInsuranceModal(value)}
                  id={id ?? ""}
                  docId={id ?? ""}
                />
              )}
              {openedModalId === ModalId.SUBMISSION_SUMMARY_MODAL &&
                metaData &&
                "data" in metaData && (
                  <SubmissionSummary
                    hideQaSecondStpAndComments
                    onClose={() => {
                      dispatch(closeModalNew());
                    }}
                    metaData={metaData as { data: any }}
                    onSuccess={() => {
                      dispatch(closeModalNew());
                    }}
                  />
                )}
              {openedModalId === ModalId.ASTERA_SUBMISSION_MODAL &&
                metaData &&
                "data" in metaData && (
                  <AsterasubmissionModal
                    onClose={() => dispatch(closeModalNew())}
                    metaData={metaData as { data: any }}
                  />
                )}
            </div>
          </div>
        )
      )}
    </>
  );
};

export default PharmaPaFormLayout;
